import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CreditCard, Lock, Check, Sparkles, ShieldCheck } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { cn } from '../../lib/utils';

const fmtCard = (v) =>
  v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
const fmtExp = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

/** Valid month (01-12) AND not in the past. */
const validExpiry = (mmYY) => {
  const m = /^(\d{2})\/(\d{2})$/.exec(mmYY);
  if (!m) return false;
  const month = Number(m[1]);
  const yy = Number(m[2]);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const curYY = now.getFullYear() % 100;
  const curM = now.getMonth() + 1;
  if (yy < curYY) return false;
  if (yy === curYY && month < curM) return false;
  return true;
};

/**
 * Realistic (but fake) checkout modal for demo billing. Collects card details,
 * simulates a processing step, then runs `onPaid()` to apply the upgrade.
 * No real payment occurs.
 */
export default function DemoCheckout({ open, plan, onClose, onPaid }) {
  const [card, setCard] = useState('');
  const [exp, setExp] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');
  const [step, setStep] = useState('form'); // form | processing | success
  const [err, setErr] = useState('');

  if (!plan) return null;

  const reset = () => {
    setCard('');
    setExp('');
    setCvc('');
    setName('');
    setStep('form');
    setErr('');
  };
  const close = () => {
    if (step === 'processing') return; // don't allow closing mid-payment
    onClose();
    setTimeout(reset, 200);
  };

  const fillTest = () => {
    setCard('4242 4242 4242 4242');
    setExp('12/34');
    setCvc('123');
    setName((n) => n || 'Demo User');
    setErr('');
  };

  const pay = async () => {
    if (card.replace(/\s/g, '').length < 16 || cvc.length < 3 || !name.trim()) {
      setErr('Enter all card details — or tap “Use demo card”.');
      return;
    }
    if (!validExpiry(exp)) {
      setErr('Card expiry must be a valid date in the future.');
      return;
    }
    setErr('');
    setStep('processing');
    try {
      await new Promise((r) => setTimeout(r, 1700)); // simulate the charge
      await onPaid();
      setStep('success');
      setTimeout(close, 1500);
    } catch {
      setStep('form');
      setErr('Payment failed — please try again.');
    }
  };

  const field =
    'glass h-11 w-full rounded-lg px-3.5 text-sm text-content placeholder:text-content-subtle focus-visible:ring-2 focus-visible:ring-brand-400';

  return (
    <Modal open={open} onClose={close} title="Complete your upgrade">
      {/* demo banner */}
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-brand-500/30 bg-brand-500/10 px-3 py-2 text-xs text-brand-200">
        <Sparkles className="h-4 w-4" /> Demo checkout — no real card is charged.
      </div>

      {/* plan summary */}
      <div className="mb-5 flex items-center justify-between rounded-xl border border-border/70 bg-white/[0.02] p-4">
        <div>
          <p className="text-sm text-content-muted">Aether {plan.name}</p>
          <p className="text-xs text-content-subtle">Billed monthly · cancel anytime</p>
        </div>
        <p className="font-display text-2xl font-semibold">
          ${plan.price}
          <span className="text-sm font-normal text-content-subtle">/mo</span>
        </p>
      </div>

      <AnimatePresence mode="wait">
        {step === 'form' && (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            <input
              className={field}
              placeholder="Name on card"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
            <div className="relative">
              <CreditCard className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle" />
              <input
                className={cn(field, 'pl-10 font-mono tracking-wider')}
                placeholder="4242 4242 4242 4242"
                inputMode="numeric"
                value={card}
                onChange={(e) => setCard(fmtCard(e.target.value))}
              />
            </div>
            <div className="flex gap-3">
              <input
                className={cn(field, 'font-mono')}
                placeholder="MM/YY"
                inputMode="numeric"
                value={exp}
                onChange={(e) => setExp(fmtExp(e.target.value))}
              />
              <input
                className={cn(field, 'font-mono')}
                placeholder="CVC"
                inputMode="numeric"
                maxLength={4}
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </div>

            <button
              onClick={fillTest}
              className="text-xs text-brand-300 transition-colors hover:text-brand-200"
            >
              Use demo card →
            </button>

            {err && <p className="text-xs text-danger">{err}</p>}

            <Button onClick={pay} size="lg" className="mt-1 w-full" icon={Lock}>
              Pay ${plan.price}/mo
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-center text-xs text-content-subtle">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured demo · powered by Aether
            </p>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-4 py-8"
          >
            <Spinner size={40} />
            <p className="text-sm text-content-muted">Processing payment…</p>
          </motion.div>
        )}

        {step === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-3 py-8 text-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 16 }}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-success/20"
            >
              <Check className="h-8 w-8 text-success" />
            </motion.div>
            <p className="font-semibold">You're on {plan.name}! 🎉</p>
            <p className="text-sm text-content-muted">Your limits have been raised.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
}
